import { formatPrice, type Language, type OrderDto, type OrderPlaceDto } from '@posilka/shared';
import { openLink, openTelegramLink } from '@telegram-apps/sdk-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '@posilka/client/api/client';
import { cancelOrder, useOrder, useOrderMutation } from '@posilka/client/api/orders';
import { declineOffer } from '@posilka/client/api/trips';
import { cityName } from '@posilka/client/lib/cities';
import { MainAction } from '@/components/MainAction';
import { Row, Section } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { ErrorState } from '@/components/States';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { StatusBadge } from '@/components/StatusBadge';
import { Timeline } from '@/components/Timeline';
import { AdminOrderTools } from '@/features/order/AdminOrderTools';
import { DeliveryCode } from '@/features/order/DeliveryCode';
import { draftFromOrder, useDraft } from '@/features/new-order/draft';
import { navigatorLinks } from '@/features/order/navigation';
import { OrderFeedback } from '@/features/order/OrderFeedback';
import { RaisePrice } from '@/features/order/RaisePrice';
import { haptic } from '@/lib/telegram';
import { formatDateTime, formatWindow } from '@posilka/client/lib/time';

const openExternal = (url: string) => {
  if (openLink.isAvailable()) openLink(url);
  else window.open(url, '_blank');
};

function Place({
  title,
  place,
  city,
  last,
}: {
  title: string;
  place: OrderPlaceDto;
  city: string;
  last?: boolean;
}) {
  const { t } = useTranslation();
  const nav = navigatorLinks(place.lat, place.lng);
  return (
    <div className="relative flex gap-3.5 px-5 py-4">
      {/* Yo'l: A nuqtadan B ga punktir chiziq. */}
      <div className="flex flex-col items-center pt-1">
        <span
          className={`h-3.5 w-3.5 shrink-0 rounded-full border-[3px] ${last ? 'border-lime bg-lime' : 'border-lime bg-surface'}`}
        />
        {!last && <span className="mt-1 w-px flex-1 border-l-2 border-dashed border-line" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-muted uppercase">{title}</p>
        <p className="font-display font-bold">{city}</p>
        <p className="text-sm">{place.address ?? t('order.addressHidden')}</p>
        {place.address && (
          <div className="mt-2.5 flex flex-wrap gap-2 text-sm">
            <a href={nav.app} className="btn-secondary px-3.5 py-1.5 text-[13px]">
              🧭 {t('order.openNavigator')}
            </a>
            <button
              type="button"
              className="btn-secondary px-3.5 py-1.5 text-[13px]"
              onClick={() => openExternal(nav.web)}
            >
              🗺 {t('order.openMap')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function OrderPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const navigate = useNavigate();
  const order = useOrder(id);
  const errorMessage = useErrorMessage();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const cancel = useOrderMutation(() => cancelOrder(id));
  const decline = useOrderMutation(() => declineOffer(id));
  const accept = useOrderMutation(() => api<OrderDto>(`/orders/${id}/accept`, { method: 'POST' }));
  const release = useOrderMutation(() =>
    api<OrderDto>(`/orders/${id}/release`, { method: 'POST' }),
  );

  if (order.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40" />
        <Skeleton className="h-32" />
      </div>
    );
  }
  if (order.isError) return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;

  const o = order.data;
  const v = o.viewer;
  const showAttempts = o.codeAttemptsLeft !== null && v.role !== 'public';
  const mutationError = [cancel, accept, release].find((m) => m.isError)?.error;
  const run = (m: { mutate: (v: void, o: object) => void }) =>
    m.mutate(undefined, { onSuccess: () => haptic.success(), onError: () => haptic.error() });

  // MainButton: har rol uchun asosiy amal.
  let main: { text: string; onClick: () => void; loading?: boolean } | null = null;
  if (v.canAccept)
    main = { text: t('driver.accept'), onClick: () => run(accept), loading: accept.isPending };
  else if (v.canPickup)
    main = {
      text: t('driver.pickup'),
      onClick: () => void navigate(`/driver/orders/${id}/pickup`),
    };
  else if (v.canDeliver)
    main = {
      text: t('driver.deliver'),
      onClick: () => void navigate(`/driver/orders/${id}/deliver`),
    };

  return (
    <>
      <div className="card glow mb-4 animate-fade-up p-5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-mono text-sm font-semibold text-muted">{o.publicNumber}</h1>
          <StatusBadge status={o.status} />
        </div>
        <p className="mt-3 font-display text-2xl leading-tight font-bold tracking-tight">
          {cityName(o.fromCity, lang)} <span className="text-accent">→</span>{' '}
          {cityName(o.toCity, lang)}
        </p>
        <p className="mt-1 text-sm text-muted">{formatWindow(o.pickupFrom, o.pickupTo, lang)}</p>
        <div className="mt-4 flex items-end justify-between gap-3 border-t border-dashed border-line pt-4">
          <span className="text-sm text-muted">
            {t(`category.${o.category}`)} · {t(`size.${o.sizeClass}.title`)}
          </span>
          <span className="font-display text-2xl font-bold whitespace-nowrap">
            {formatPrice(o.price, t('common.currency'))}
          </span>
        </div>
      </div>

      {o.photoUrls.length > 0 && (
        <div className="-mx-4 mb-4 flex snap-x gap-2 overflow-x-auto px-4">
          {o.photoUrls.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 snap-start"
            >
              <img
                src={url}
                alt=""
                className="h-40 w-40 rounded-3xl border border-line object-cover"
              />
            </a>
          ))}
        </div>
      )}

      {v.canViewCode && <DeliveryCode orderId={o.id} />}

      <Section>
        <Place title={t('order.pickupShort')} place={o.pickup} city={cityName(o.fromCity, lang)} />
        <Place
          title={t('order.dropoffShort')}
          place={o.dropoff}
          city={cityName(o.toCity, lang)}
          last
        />
      </Section>

      {(o.description || o.recipient || showAttempts) && (
        <Section>
          {o.description && <Row label={t('order.description')} value={o.description} />}
          {o.recipient && (
            <Row
              label={t('order.recipient')}
              value={
                <a href={`tel:${o.recipient.phone}`} className="text-link">
                  {o.recipient.name}, {o.recipient.phone}
                </a>
              }
            />
          )}
          {showAttempts && <Row label={t('driver.attemptsLeft')} value={o.codeAttemptsLeft} />}
        </Section>
      )}

      {o.driver && v.role !== 'driver' && (
        <Section title={t('order.driver')}>
          <Link to={`/drivers/${o.driver.id}`} className="block">
            <Row
              label={
                <span>
                  {o.driver.firstName}
                  {o.driver.rating.verified && ' ✅'}
                  {o.driver.rating.average ? ` · ⭐ ${o.driver.rating.average}` : ''}
                  <span className="ml-1 text-link">›</span>
                </span>
              }
              value={`${o.driver.carModel}, ${o.driver.carColor}`}
            />
          </Link>
          <Row
            label={t('driver.plate')}
            value={<span className="font-mono">{o.driver.carPlate}</span>}
          />
          {o.driver.phone && (
            <a href={`tel:${o.driver.phone}`} className="btn-secondary m-3 flex">
              📞 {t('order.call')} {o.driver.phone}
            </a>
          )}
        </Section>
      )}

      {o.sender && v.role !== 'sender' && (
        <Section title={t('order.sender')}>
          <Row
            label={`${o.sender.firstName}${o.sender.rating.average ? ` · ⭐ ${o.sender.rating.average}` : ''}`}
            value={t('order.completedCount', { count: o.sender.rating.completedOrders })}
          />
          {o.sender.phone && (
            <a href={`tel:${o.sender.phone}`} className="btn-secondary m-3 flex">
              📞 {t('order.call')} {o.sender.phone}
            </a>
          )}
        </Section>
      )}

      {v.role === 'public' && (
        <p className="mb-4 px-1 text-sm text-hint">{t('driver.detailsAfterAccept')}</p>
      )}

      {o.pickupPhotoUrl && (
        <Section title={t('order.pickupPhoto')}>
          <img src={o.pickupPhotoUrl} alt="" className="max-h-72 w-full object-cover" />
        </Section>
      )}

      {o.dispute && (
        <Section title={`⚖️ ${t('dispute.title')}`}>
          <Row label={t(`dispute.outcomes.${o.dispute.outcome}`)} />
          <p className="px-5 pb-4 text-sm whitespace-pre-wrap text-hint">{o.dispute.note}</p>
        </Section>
      )}

      {o.deliveryPhotoUrl && (
        <Section title={t('order.deliveryPhoto')}>
          <img src={o.deliveryPhotoUrl} alt="" className="max-h-72 w-full object-cover" />
        </Section>
      )}

      {o.history.length > 0 && (
        <Section title={t('order.history')}>
          <Timeline history={o.history} />
        </Section>
      )}

      {o.cancelledAt && (
        <p className="mb-4 px-1 text-sm text-hint">
          {t('order.cancelledAt', { date: formatDateTime(o.cancelledAt, lang) })}
          {o.cancelReason ? ` — ${o.cancelReason}` : ''}
        </p>
      )}

      <OrderFeedback order={o} />
      <AdminOrderTools order={o} />

      {mutationError && (
        <p className="mb-3 px-1 text-sm text-destructive">{errorMessage(mutationError)}</p>
      )}

      <div className="mb-24 space-y-2">
        {v.canRelease && (
          <button
            type="button"
            className="btn-secondary w-full text-destructive"
            onClick={() => run(release)}
          >
            {t('driver.release')}
          </button>
        )}
        {v.canCancel &&
          (confirmCancel ? (
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary flex-1"
                onClick={() => setConfirmCancel(false)}
              >
                {t('common.no')}
              </button>
              <button
                type="button"
                className="btn-secondary flex-1 text-destructive"
                onClick={() => run(cancel)}
                disabled={cancel.isPending}
              >
                {t('order.confirmCancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary w-full text-destructive"
              onClick={() => setConfirmCancel(true)}
            >
              {t('order.cancel')}
            </button>
          ))}
        {o.offer && v.role !== 'public' && (
          <p className="card px-5 py-4 text-sm">
            🗓{' '}
            {t('trips.offerPending', {
              name: o.offer.driverFirstName,
              until: formatDateTime(o.offer.expiresAt, lang),
            })}
          </p>
        )}
        {v.canDeclineOffer && (
          <button
            type="button"
            className="btn-secondary w-full text-destructive"
            onClick={() => run(decline)}
            disabled={decline.isPending}
          >
            {t('trips.declineOffer')}
          </button>
        )}
        {v.role === 'sender' && o.status === 'OPEN' && <RaisePrice order={o} />}
        {o.trackingUrl && v.role === 'sender' && (
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => {
              haptic.tap();
              const share = `https://t.me/share/url?url=${encodeURIComponent(o.trackingUrl!)}&text=${encodeURIComponent(t('order.shareTrackingText', { number: o.publicNumber }))}`;
              if (openTelegramLink.isAvailable()) openTelegramLink(share);
              else window.open(share, '_blank');
            }}
          >
            📤 {t('order.shareTracking')}
          </button>
        )}
        {v.role === 'sender' && (
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => {
              haptic.tap();
              useDraft.setState(draftFromOrder(o));
              void navigate('/orders/new');
            }}
          >
            🔁 {t('order.repeat')}
          </button>
        )}
        {v.role === 'public' && !v.canAccept && (
          <p className="px-1 text-center text-sm text-hint">
            <Link to="/profile" className="text-link">
              {t('driver.phoneNeeded')}
            </Link>
          </p>
        )}
      </div>

      {main && <MainAction text={main.text} onClick={main.onClick} loading={main.loading} />}
    </>
  );
}
